import React, { FormEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  DialogDescription,
} from "../../../components/ui/Dialog";
import Button from "../../../components/ui/Button";
import Input from "../../../components/ui/Input";
import { Checkbox } from "../../../components/ui/Checkbox";
import AppIcon from "../../../components/AppIcon";
import { resetUserPassword } from "../../../api/General/account";

type ResetPasswordDialogProps = Readonly<{
  open: boolean;
  onOpenChange: (open: boolean) => void;
  userId: string;
  userDisplayName: string;
}>;

type Mode = "generate" | "custom";

// Mirrors the server-side ASP.NET Identity PasswordOptions defaults.
function evaluatePasswordRules(pw: string) {
  return {
    length: pw.length >= 10,
    upper: /[A-Z]/.test(pw),
    lower: /[a-z]/.test(pw),
    digit: /\d/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  };
}

const ResetPasswordDialog: React.FC<ResetPasswordDialogProps> = ({
  open,
  onOpenChange,
  userId,
  userDisplayName,
}) => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("generate");
  const [customPassword, setCustomPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [mustChange, setMustChange] = useState(false);
  const [revokeSessions, setRevokeSessions] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const [issuedPassword, setIssuedPassword] = useState<string | null>(null);
  const [issuedMustChange, setIssuedMustChange] = useState(false);
  const [revokedCount, setRevokedCount] = useState(0);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      setMode("generate");
      setCustomPassword("");
      setShowPassword(false);
      setMustChange(false);
      setRevokeSessions(true);
      setSubmitting(false);
      setError("");
      setIssuedPassword(null);
      setIssuedMustChange(false);
      setRevokedCount(0);
      setCopied(false);
    }
  }, [open]);

  const rules = useMemo(() => evaluatePasswordRules(customPassword), [customPassword]);
  const customPasswordValid = Object.values(rules).every(Boolean);

  const isSuccess = issuedPassword !== null;
  const dismissable = !isSuccess && !submitting;

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError("");

    if (mode === "custom" && !customPasswordValid) {
      setError(t("Password does not meet the requirements"));
      return;
    }

    setSubmitting(true);
    try {
      const response = await resetUserPassword({
        userId,
        newPassword: mode === "custom" ? customPassword : undefined,
        mustChangeOnNextLogin: mustChange,
        revokeActiveSessions: revokeSessions,
      });
      if (response.errors?.length) {
        setError(response.errors[0]?.description ?? t("Failed to reset password"));
        return;
      }
      const payload = response.payload;
      if (!payload) {
        setError(t("Failed to reset password"));
        return;
      }
      setIssuedPassword(payload.generatedPassword);
      setIssuedMustChange(payload.mustChangeOnNextLogin);
      setRevokedCount(payload.revokedSessionCount);
    } catch (err: unknown) {
      const e = err as { errors?: Array<{ description?: string }>; message?: string };
      setError(e?.errors?.[0]?.description ?? e?.message ?? t("Failed to reset password"));
    } finally {
      setSubmitting(false);
    }
  };

  const handleCopy = async () => {
    if (!issuedPassword) return;
    try {
      await navigator.clipboard.writeText(issuedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard fails in non-secure contexts; user can still select the text.
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        // Disallow closing once the password has been shown (would lose it) and
        // during in-flight submission (would leave the API call orphaned).
        if (!next && !dismissable) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        size="sm"
        hideCloseButton={!dismissable}
        onEscapeKeyDown={(e) => { if (!dismissable) e.preventDefault(); }}
        onPointerDownOutside={(e) => { if (!dismissable) e.preventDefault(); }}
      >
        <DialogHeader>
          <DialogTitle>
            {isSuccess
              ? t("Password reset successfully")
              : t("Reset password for {{name}}", { name: userDisplayName })}
          </DialogTitle>
          {!isSuccess && (
            <DialogDescription>
              {t("Choose how to set the new password and how this should affect their active sessions.")}
            </DialogDescription>
          )}
        </DialogHeader>

        {isSuccess ? (
          <>
            <DialogBody className="space-y-5">
              <p className="text-sm text-foreground">
                {t("Copy this password now. It will not be shown again, and it is not included in the notification email sent to the user.")}
              </p>

              {/* Password hero block */}
              <div className="rounded-lg border-2 border-primary/40 bg-primary/5 p-5">
                <div className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground mb-2">
                  {t("New password")}
                </div>
                <div className="flex items-stretch gap-2">
                  <code
                    className="flex-1 select-all break-all rounded-md bg-card px-4 py-3 font-mono text-xl font-semibold tracking-wider text-foreground border border-border"
                    aria-label={t("Generated password")}
                  >
                    {issuedPassword}
                  </code>
                  <Button
                    type="button"
                    variant="default"
                    size="lg"
                    onClick={handleCopy}
                    aria-label={t("Copy password")}
                    aria-live="polite"
                  >
                    <AppIcon name={copied ? "Check" : "Copy"} className="h-4 w-4" />
                    <span className="ms-2">{copied ? t("Copied") : t("Copy")}</span>
                  </Button>
                </div>
              </div>

              {/* Forward-looking guidance, not a settings echo */}
              <ul className="space-y-2 text-sm text-muted-foreground">
                <li className="flex items-start gap-2">
                  <AppIcon name="Mail" className="h-4 w-4 mt-0.5 text-muted-foreground/80" />
                  <span>
                    {t("A notification email was sent to {{name}}. The password is not in that email.", { name: userDisplayName })}
                  </span>
                </li>
                {issuedMustChange && (
                  <li className="flex items-start gap-2">
                    <AppIcon name="KeyRound" className="h-4 w-4 mt-0.5 text-muted-foreground/80" />
                    <span>{t("They will be asked to choose a new password on next sign-in.")}</span>
                  </li>
                )}
                {revokedCount > 0 && (
                  <li className="flex items-start gap-2">
                    <AppIcon name="LogOut" className="h-4 w-4 mt-0.5 text-muted-foreground/80" />
                    <span>
                      {t("{{count}} active session(s) were ended.", { count: revokedCount })}
                    </span>
                  </li>
                )}
              </ul>
            </DialogBody>
            <DialogFooter>
              <Button type="button" onClick={() => onOpenChange(false)}>
                {t("I've saved the password")}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <form onSubmit={handleSubmit}>
            <DialogBody className="space-y-5">
              <fieldset className="space-y-2">
                <legend className="text-sm font-medium text-foreground mb-1.5">
                  {t("How to set the new password")}
                </legend>
                <ModeCard
                  selected={mode === "generate"}
                  onSelect={() => setMode("generate")}
                  title={t("Generate strong password")}
                  description={t("The system creates a random secure password. You see it once, then share it with the user.")}
                  inputName="reset-mode"
                  inputValue="generate"
                />
                <ModeCard
                  selected={mode === "custom"}
                  onSelect={() => setMode("custom")}
                  title={t("Set custom password")}
                  description={t("Type a password yourself. Useful when you need to communicate it verbally.")}
                  inputName="reset-mode"
                  inputValue="custom"
                />
              </fieldset>

              {mode === "custom" && (
                <div className="space-y-2">
                  <div className="relative">
                    <Input
                      type={showPassword ? "text" : "password"}
                      value={customPassword}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCustomPassword(e.target.value)}
                      placeholder={t("Type new password")}
                      className="pe-10"
                      autoComplete="new-password"
                      aria-describedby="reset-password-rules"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((p) => !p)}
                      className="absolute end-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? t("Hide password") : t("Show password")}
                      aria-pressed={showPassword}
                    >
                      <AppIcon name={showPassword ? "EyeOff" : "Eye"} className="h-4 w-4" />
                    </button>
                  </div>
                  <ul id="reset-password-rules" className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                    <RuleItem label={t("At least 10 characters")} ok={rules.length} />
                    <RuleItem label={t("Uppercase letter")} ok={rules.upper} />
                    <RuleItem label={t("Lowercase letter")} ok={rules.lower} />
                    <RuleItem label={t("Number")} ok={rules.digit} />
                    <RuleItem label={t("Symbol")} ok={rules.symbol} />
                  </ul>
                </div>
              )}

              <div className="space-y-3 pt-1 border-t border-border pt-4">
                <Checkbox
                  checked={mustChange}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMustChange(e.target.checked)}
                  label={t("Require user to change password on next sign-in")}
                />
                <Checkbox
                  checked={revokeSessions}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setRevokeSessions(e.target.checked)}
                  label={t("End the user's active sessions on every device")}
                />
              </div>

              {error && (
                <p className="text-sm text-destructive" role="alert">
                  {error}
                </p>
              )}
            </DialogBody>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => onOpenChange(false)}
                disabled={submitting}
              >
                {t("Cancel")}
              </Button>
              <Button
                type="submit"
                variant="destructive"
                loading={submitting}
                disabled={submitting || (mode === "custom" && !customPasswordValid)}
              >
                {mode === "generate" ? t("Generate & reset") : t("Set new password")}
              </Button>
            </DialogFooter>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

type ModeCardProps = {
  selected: boolean;
  onSelect: () => void;
  title: string;
  description: string;
  inputName: string;
  inputValue: string;
};

const ModeCard: React.FC<ModeCardProps> = ({ selected, onSelect, title, description, inputName, inputValue }) => (
  <label
    className={`flex items-start gap-3 cursor-pointer rounded-lg border p-3 transition-colors ${
      selected
        ? "border-primary bg-primary/5 ring-1 ring-primary/30"
        : "border-border hover:border-muted-foreground/40 hover:bg-muted/30"
    }`}
  >
    <input
      type="radio"
      name={inputName}
      value={inputValue}
      checked={selected}
      onChange={onSelect}
      className="mt-1 h-4 w-4 accent-primary"
    />
    <div className="flex-1">
      <div className={`text-sm font-medium ${selected ? "text-primary" : "text-foreground"}`}>{title}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{description}</div>
    </div>
  </label>
);

type RuleItemProps = { label: string; ok: boolean };

const RuleItem: React.FC<RuleItemProps> = ({ label, ok }) => (
  <li className={`flex items-center gap-1.5 ${ok ? "text-foreground" : "text-muted-foreground"}`}>
    <AppIcon
      name={ok ? "Check" : "Circle"}
      className={`h-3 w-3 ${ok ? "text-success" : "text-muted-foreground/50"}`}
    />
    <span>{label}</span>
  </li>
);

export default ResetPasswordDialog;
